import DOMPurify from 'dompurify';

/*
 * SUPPORT-708 (F-034): centralized HTML sanitizer.
 *
 * Every v-html sink in the SPA that renders admin/TinyMCE or external HTML
 * must route through this module via the v-safe-html directive. Scattering
 * DOMPurify.sanitize() calls throughout components is not allowed —
 * additions and audits should always pass through this file.
 *
 * Policy summary:
 *   - HTML only. SVG and MathML namespaces are banned; the dedicated
 *     SafeQrCode component (PR 2) handles the one legitimate SVG sink.
 *   - No script/iframe/object/embed/form/style/link/meta/base/foreignObject.
 *   - No inline event handlers or style attributes.
 *   - URIs restricted to http(s), mailto, tel, and same-origin/relative paths.
 *   - target on anchors restricted to _blank or _self only; _blank forces
 *     rel="noopener noreferrer".
 *   - TinyMCE `data-mce-*` attributes and `<!--mce:protected …-->` comments
 *     stripped.
 *   - Hooks are registered exactly once at module load (below), never on
 *     each sanitize call.
 */

const ALLOWED_TAGS = [
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'div',
    'em',
    'figure',
    'figcaption',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'li',
    'ol',
    'p',
    'pre',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
];

const ALLOWED_ATTR = [
    'alt',
    'class',
    'colspan',
    'dir',
    'href',
    'lang',
    'rel',
    'rowspan',
    'src',
    'tabindex',
    'target',
    'title',
];

const FORBID_TAGS = [
    'script',
    'iframe',
    'object',
    'embed',
    'form',
    'style',
    'link',
    'meta',
    'base',
    'noscript',
    'template',
    // SVG namespace family — banned outright; dedicated SafeQrCode (PR 2)
    // covers the sole legitimate SVG sink.
    'svg',
    'foreignobject',
    'use',
    'desc',
    'symbol',
    'circle',
    'ellipse',
    'line',
    'polygon',
    'polyline',
    'rect',
    'path',
    'g',
    'defs',
    'lineargradient',
    'radialgradient',
    'stop',
    'marker',
    'mask',
    'pattern',
    'clippath',
    'text',
    'tspan',
    // MathML namespace family
    'math',
    'annotation-xml',
    'mi',
    'mo',
    'mn',
    'ms',
    'mtext',
    'mrow',
    'msqrt',
    'mfrac',
    'mstyle',
    'merror',
    'mspace',
    'mpadded',
    'mphantom',
];

const FORBID_ATTR = [
    'style',
    'srcset',
    'onerror',
    'onload',
    'onclick',
    'onmouseover',
    'onfocus',
    'onblur',
    'onchange',
    'onsubmit',
    'onkeydown',
    'onkeyup',
    'onkeypress',
    'onanimationstart',
    'onanimationend',
    'onanimationiteration',
    'ontransitionend',
    'onpointerdown',
    'onpointerup',
    'onpointerover',
    'onpointermove',
    'onpointerout',
    'onpointercancel',
    'onpointerenter',
    'onpointerleave',
    'formaction',
];

const SAFE_URI_REGEX = /^(?:https?:|mailto:|tel:|#|\/|\.\/|\.\.\/)/i;

// Anchor `target` values permitted in sanitized rich content. `_parent` and
// `_top` are rejected outright — they can escape framed contexts and offer
// nothing legitimate to prose content.
const ALLOWED_TARGETS = new Set(['_blank', '_self']);

/**
 * Register the DOMPurify hooks exactly once, synchronously at module import.
 * DOMPurify's hook list is process-global; installing multiple copies would
 * duplicate side-effects (rel additions, attribute stripping) on every
 * sanitize call. Everything the hook needs is closed-over at module load, so
 * subsequent imports of this file share the same registration.
 */
function registerHooks() {
    // DOMPurify v3 strips `target` from anchors when ALLOWED_URI_REGEXP is set
    // even if `target` appears in ALLOWED_ATTR / ADD_ATTR. Force-keep only the
    // approved target values.
    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
        if (data.attrName !== 'target' || node.tagName !== 'A') {
            return;
        }
        const value = String(data.attrValue || '').toLowerCase();
        if (ALLOWED_TARGETS.has(value)) {
            data.attrValue = value;
            data.forceKeepAttr = true;
            return;
        }
        // Invalid or disallowed target ("_parent", "_top", garbage, empty):
        // drop the attribute entirely.
        data.keepAttr = false;
    });

    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (!(node instanceof Element)) {
            return;
        }

        if (node.tagName === 'A' && node.hasAttribute('target')) {
            // Normalize target to lowercase so the allowlist check we ran in
            // uponSanitizeAttribute is reflected in the rendered attribute.
            const raw = node.getAttribute('target') || '';
            const normalized = raw.toLowerCase();
            if (raw !== normalized) {
                node.setAttribute('target', normalized);
            }
            if (normalized === '_blank') {
                node.setAttribute('rel', 'noopener noreferrer');
            }
        }

        for (const attr of ['href', 'src']) {
            const value = node.getAttribute(attr);
            if (value && !SAFE_URI_REGEX.test(value.trim())) {
                node.removeAttribute(attr);
            }
        }

        for (const name of node.getAttributeNames()) {
            if (name.startsWith('data-mce-')) {
                node.removeAttribute(name);
            }
        }
    });

    DOMPurify.addHook('uponSanitizeComment', (node) => {
        // Drop TinyMCE's <!--mce:protected …--> and any other stray comments.
        node.remove?.();
    });
}

registerHooks();

const BASE_CONFIG = {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // DOMPurify v3 needs ADD_ATTR to keep `target` alongside ALLOWED_URI_REGEXP;
    // the uponSanitizeAttribute hook then enforces the ALLOWED_TARGETS
    // allowlist. See SUPPORT-708 hardening notes.
    ADD_ATTR: ['target'],
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEX,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    // HTML-only parser prevents namespace-confusion payloads that rely on the
    // parser flipping into SVG/MathML foreign-content mode.
    PARSER_MEDIA_TYPE: 'text/html',
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
    SANITIZE_DOM: true,
    USE_PROFILES: false,
};

/**
 * Sanitize an HTML string against the shared allowlist.
 *
 * @param {unknown} html
 * @returns {string} sanitized HTML — safe to feed to v-html.
 */
export function sanitizeHtml(html) {
    if (html == null) {
        return '';
    }
    if (typeof html !== 'string') {
        html = String(html);
    }
    return DOMPurify.sanitize(html, BASE_CONFIG);
}

/**
 * Test-only helper: report the effective policy so regressions can assert
 * against it.
 */
export const __policy = Object.freeze({
    ALLOWED_TAGS: Object.freeze([...ALLOWED_TAGS]),
    ALLOWED_ATTR: Object.freeze([...ALLOWED_ATTR]),
    FORBID_TAGS: Object.freeze([...FORBID_TAGS]),
    FORBID_ATTR: Object.freeze([...FORBID_ATTR]),
    ALLOWED_TARGETS: Object.freeze([...ALLOWED_TARGETS]),
    SAFE_URI_REGEX,
});
